import { AlertTriangle, Inbox, Loader2, ShieldX } from 'lucide-react';
import { Component, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api.ts';

export function Loading() {
  const { t } = useTranslation();
  return (
    <div className="empty" role="status">
      <Loader2 size={20} className="spin" />
      {t('common.loading')}
    </div>
  );
}

export function Empty({ text }: { text: string }) {
  return (
    <div className="empty">
      <Inbox size={22} />
      {text}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t, i18n } = useTranslation();
  const code = error instanceof ApiError ? error.code : 'internal';
  const forbidden = code === 'forbidden';
  // When the server says why (a known reason in the details), say that instead of "something went wrong".
  const reason = (
    error instanceof ApiError && Array.isArray(error.details)
      ? (error.details as { code?: string; detail?: string }[])
      : []
  ).find((d) => d.code && i18n.exists(`errors.${d.code}`));
  return (
    <div className="empty" role="alert">
      {forbidden ? <ShieldX size={22} /> : <AlertTriangle size={22} />}
      {reason ? (
        <span data-testid="error-reason">
          {t(`errors.${reason.code}`)}
          {reason.detail && (
            <span className="error-detail mono" dir="ltr">
              {reason.detail}
            </span>
          )}
        </span>
      ) : forbidden ? (
        t('common.forbidden')
      ) : code === 'unavailable' ? (
        t('common.unavailable')
      ) : (
        t('common.error')
      )}
      {onRetry && !forbidden && (
        <button className="btn btn-ghost btn-sm" onClick={onRetry}>
          {t('common.retry')}
        </button>
      )}
    </div>
  );
}

/**
 * Keeps one broken section (a side panel, a card) from taking the whole page down: it shows the usual error state
 * in its place and logs the cause. `resetKey` clears the error when the section shows something else.
 */
export class SectionBoundary extends Component<
  { children: ReactNode; resetKey?: unknown },
  { failed: boolean }
> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override componentDidCatch(error: unknown) {
    console.error(error);
  }
  override componentDidUpdate(prev: { resetKey?: unknown }) {
    if (this.state.failed && prev.resetKey !== this.props.resetKey) this.setState({ failed: false });
  }
  override render() {
    return this.state.failed ? <ErrorState error={null} /> : this.props.children;
  }
}
